<template>
    <BasicHeader pretitle="Administration" title="Credentials" buttonText="Add Credential" :buttonDisabled="!namespace"
        @btnClick="showAddCredential" :buttonRoles="editRoles" />
    <div class="container-fluid">
        <div class="row">
            <div class="col-md-6 col-lg-4">
                <InputField label="Choose a Service" type="select" :options="serviceOptions" v-model="service" />
            </div>
            <!-- amdp keeps its credentials per sandbox, every other service uses a plain namespace -->
            <div v-if="usesSandboxNamespace" class="col-md-6 col-lg-4">
                <InputField :label="`Choose a ${serviceConfig.namespaceLabel}`" type="select" :options="sandboxOptions"
                    v-model="namespace" />
            </div>
            <template v-else>
                <form @submit.prevent="onNamespaceSubmit" class="col-md-6 col-lg-4">
                    <InputField :label="serviceConfig.namespaceLabel" type="text" v-model="namespaceInput"
                        :placeholder="`Enter a ${serviceConfig.namespaceLabel.toLowerCase()}`"
                        :editable="!namespaceDisabled" :helpText="namespaceHelpText"
                        :toolTip="`non-aMDP service credentials are only supported under the ${this.serviceConfig.namespaceDefault} namespace.`"/>
                </form>
            </template>
            <div class="col-md-6 col-lg-4 text-end">
                <label class="form-label d-block">&nbsp;</label>
                <button type="button" class="btn btn-primary" :disabled="!namespaceInput || loading"
                    @click="onNamespaceSubmit">
                    <i class="fa-solid fa-refresh"></i>
                </button>
            </div>
        </div>

        <div v-if="error" class="alert alert-danger" role="alert">
            {{ error }}
        </div>

        <div v-if="!namespace" class="alert alert-secondary" role="alert">
            <template v-if="usesSandboxNamespace && !sandboxOptions.length">
                No sandbox available for this client!
            </template>
            <template v-else>
                Choose a {{ serviceConfig.namespaceLabel.toLowerCase() }} to list its credentials.
            </template>
        </div>
        <div v-else class="card">
            <Datatable :tableId="`aquacredCredentials`" :columns="columns" uniqueField="name" :data="credentialList"
                :deletable="canManageCredentials" :selectCheckboxes="canManageCredentials" deleteTxt="Delete"
                @deleteRows="onDeleteCredentials" :compact="true" :detailColumn="detailColumn" :showLoading="loading"
                :ignoreCache="true" />
        </div>
    </div>
</template>

<script>
import BasicHeader from "../../components/BasicHeader.vue";
import Datatable from "../../components/Datatable.vue";
import InputField from "../../components/InputField.vue";
import { mapActions, mapState } from "vuex";
import {
    AQUACRED_SERVICES,
    DEFAULT_AQUACRED_SERVICE,
    getCredentialType,
    getServiceConfig,
} from "../../utils/aquacred";

export default {
    name: "AquacredCredentials",
    components: { BasicHeader, Datatable, InputField },
    data() {
        return {
            columns: [
                {
                    data: "name",
                    title: "Credential Name",
                },
                {
                    data: "type",
                    title: "Credential Type",
                },
            ],
            namespaceInput: this.$route.query.namespace
                || getServiceConfig(this.$route.query.service || DEFAULT_AQUACRED_SERVICE).namespaceDefault
                || "",
            loading: false,
            error: null,
            serviceOptions: AQUACRED_SERVICES.map((s) => ({ label: s.label, value: s.value })),
            editRoles: [1, 1.5, 3], // aqfer admin, aqfer manager or client admin
        };
    },
    computed: {
        ...mapState({
            aquacredCredentials: (state) => state.amdp.aquacredCredentials,
        }),
        cid() {
            return this.$route.params.cid || this.$store.state.currentClient.cid;
        },
        // Service and namespace live in the URL so the page can be shared/bookmarked as-is
        service: {
            get() {
                return this.$route.query.service || DEFAULT_AQUACRED_SERVICE;
            },
            set(value) {
                if (!value || value === this.service) return;
                const config = getServiceConfig(value);
                // pick a sensible namespace for the newly selected service - the sandbox
                // picker's first option, or the service's configured default namespace
                let namespace = config.namespaceType === "sandbox" ? this.defaultSandbox : (config.namespaceDefault || "");
                this.namespaceInput = namespace;
                this.applySelection(value, namespace);
            },
        },
        namespace: {
            get() {
                return this.$route.query.namespace || "";
            },
            set(value) {
                if (!value || value === this.namespace) return;
                this.applySelection(this.service, value);
            },
        },
        serviceConfig() {
            return getServiceConfig(this.service);
        },
        usesSandboxNamespace() {
            return this.serviceConfig.namespaceType === "sandbox";
        },
        // some services only accept their default namespace, so the input is locked
        namespaceDisabled() {
            return !!this.serviceConfig.disabled;
        },
        namespaceHelpText() {
            return `Namespace the credentials are stored under in the ${this.serviceConfig.label} service.`;
        },
        sandboxOptions() {
            let devSandboxes = this.$store.state.sandbox.devSandboxes || [];
            let prodV2Sandboxes = this.$store.state.sandbox.prodV2Sandboxes || [];
            return [...devSandboxes, ...prodV2Sandboxes]
                .filter((sandbox) => sandbox.credential_manager === "aquacred")
                .map((sandbox) => {
                    return { label: sandbox.name + " - " + sandbox.env, value: sandbox.name };
                });
        },
        defaultSandbox() {
            const current = this.$store.state.sandbox.currentSandbox;
            if (current && this.sandboxOptions.some((o) => o.value === current)) return current;
            return this.sandboxOptions.length ? this.sandboxOptions[0].value : "";
        },
        credentialList() {
            let creds = this.aquacredCredentials?.[this.service]?.[this.namespace]?.credentials || [];
            creds.forEach((cred) => {
                cred["type"] = getCredentialType(cred);
            });
            return creds.length > 0 ? creds : null;
        },
        canManageCredentials() {
            return this.$store.getters.userRoles.isAccessByRole(this.editRoles);
        },
        detailColumn() {
            // service/namespace are part of the detail path so the link stays valid
            // when opened in a new tab or shared with another user
            const base = `/${this.cid}/admin/credentials/${encodeURIComponent(this.service)}/${encodeURIComponent(this.namespace)}/`;
            return {
                renderBaseUrl: () => base,
                identifier: "name",
            };
        },
    },
    watch: {
        // a change to either selection (including browser back/forward) reloads the
        // list - cached selections render from the store without a refetch
        "$route.query": {
            handler(query) {
                this.namespaceInput = query.namespace || "";
                console.log("route changed...")
                this.refreshCredentials();
            },
        },
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            fetchAquacredCredentials: "amdp/fetchAquacredCredentials",
            deleteAquacredCredentials: "amdp/deleteAquacredCredentials",
        }),
        applySelection(service, namespace) {
            const query = { service };
            if (namespace) query.namespace = namespace;
            this.$router.replace({
                name: "Aquacred Credentials",
                params: { cid: this.cid },
                query,
            });
        },
        onNamespaceSubmit() {
            const value = (this.namespaceInput || "").trim();
            if (!value) return;
            // re-submitting the same namespace acts as a reload
            console.log("namespace changed...")
            if (value === this.namespace) this.refreshCredentials(true);
            else this.namespace = value;
        },
        // `forceGet` skips the per-service/namespace cache - used whenever the user
        // explicitly asks for a reload or the list was just mutated
        async refreshCredentials(forceGet = false) {
            this.error = null;
            // nothing to list without a full selection; `credentialList` reads the
            // cache by service/namespace, so there is no stale list to clear here
            if (!this.service || !this.namespace) return;
            this.loading = true;
            try {
                await this.fetchAquacredCredentials({
                    service: this.service,
                    namespace: this.namespace,
                    mutation: "setAquacredCredentials",
                    forceGet,
                });
            } catch (error) {
                this.error = error?.response?.data?.error ?? error.message;
            } finally {
                this.loading = false;
            }
        },
        showAddCredential() {
            this.showModal({
                component: "AddAquacredCredential",
                data: {
                    service: this.service,
                    namespace: this.namespace,
                    allowServiceSelect: true,
                    refreshCredentials: this.onCredentialAdded,
                },
            });
        },
        // the modal can create the credential under a different service/namespace than
        // the one currently listed - follow it there so the new credential is visible
        async onCredentialAdded({ service, namespace } = {}) {
            if (service && namespace && (service !== this.service || namespace !== this.namespace)) {
                // the cache for the target selection is now stale - refetch it before
                // navigating, otherwise the watcher would render the pre-add list
                await this.fetchAquacredCredentials({
                    service,
                    namespace,
                    mutation: "setAquacredCredentials",
                    forceGet: true,
                }).catch((error) => {
                    this.error = error?.response?.data?.error ?? error.message;
                });
                this.applySelection(service, namespace);
            } else {
                this.refreshCredentials(true);
            }
        },
        onDeleteCredentials(creds) {
            let self = this;
            this.showModal({
                component: "confirm",
                data: {
                    title: "Delete Credential(s)",
                    message: `
                        Are you sure you want to <strong class="text-danger">DELETE</strong> the following credentials from
                        <strong>${self.service}</strong> / <strong>${self.namespace}</strong>?
                        <div class="delete-creds-container">
                            <ul class="list-group list-group-flush">
                                ${creds.map(cred => `
                                    <li class="list-group-item py-2 delete-cred-item fs-5">
                                        <i class="fa-solid fa-key me-2 text-warning"></i>
                                        <span class="fw-bold">${cred.name}</span>
                                    </li>
                                `).join('')}
                            </ul>
                        </div>`,
                    confirmText: "Delete",
                    confirmClass: "btn-danger",
                    cancelText: "Cancel",
                    cancelClass: "btn-secondary",
                    async confirm() {
                        try {
                            const results = await Promise.allSettled(creds.map(cred =>
                                self.deleteAquacredCredentials({
                                    service: self.service,
                                    namespace: self.namespace,
                                    credentialName: cred.name,
                                })
                            ));

                            let successfulDeletions = 0;
                            results.forEach((res, idx) => {
                                const cred = creds[idx];
                                if (res.status === "fulfilled") {
                                    successfulDeletions++;
                                } else {
                                    console.error(`Failed to delete credential: ${cred.name}\nReason: ${res.reason?.response?.data}`);
                                    self.$store.dispatch("addToast", {
                                        message: res.reason?.response?.data?.error || `Error deleting credential ${cred.name}`,
                                        type: "error",
                                    });
                                }
                            });

                            if (successfulDeletions == creds.length) {
                                self.$store.dispatch("addToast", {
                                    message: `Credential${creds.length > 1 ? "s" : ""} deleted successfully`,
                                    type: "success",
                                });
                            }

                            await self.refreshCredentials(true);
                        } catch (error) {
                            let err = error?.response?.data?.error || error?.message;
                            console.error(err);
                            self.$store.dispatch("addToast", {
                                type: "error",
                                message: "Something went wrong while deleting credentials. Please contact support.",
                            });
                        }
                    },
                },
            });
        },
    },
    beforeMount() {
        // Normalise the URL once so it always carries the full selection - a copied
        // link then reopens exactly this list.
        const service = this.$route.query.service || DEFAULT_AQUACRED_SERVICE;
        const config = getServiceConfig(service);
        let namespace = this.$route.query.namespace || "";
        if (!namespace) {
            namespace = config.namespaceType === "sandbox" ? this.defaultSandbox : (config.namespaceDefault || "");
        }
        const needsRedirect = !this.$route.params.cid // landed on the cid-less alias
            || service !== this.$route.query.service
            || namespace !== (this.$route.query.namespace || "");
        if (needsRedirect) {
            this.applySelection(service, namespace);
            return; // the redirect re-runs this hook / triggers the $route.query watcher
        }
        this.refreshCredentials();
    },
};
</script>

<style lang="scss">
/* Container to prevent modal blowout when deleting multiple items */
.delete-creds-container {
    max-height: 300px;
    overflow-y: auto;
    margin-top: 0.75rem;
}

.delete-cred-item {
    word-break: break-all;
}
</style>
